import styles from './PreviewTitle.module.scss';

import { ChevronRight, Delete } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import Fullscreen from '@mui/icons-material/Fullscreen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShareIcon from '@mui/icons-material/Share';
import { OutlinedInput, Theme, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ConvertIcon } from '@work4all/assets/icons/convert.svg';
import { ReactComponent as SettingsIcon } from '@work4all/assets/icons/settings3.svg';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { CustomToolbarMappedAction } from '../../../../dataDisplay/actions-bar/ToolbarTypes';
import {
  ConvertPopover,
  IConvertPopoverProps,
} from '../../../convert-popover/ConvertPopover';
import { Tooltip } from '../../../tooltip/Tooltip';
import { MoreMenu } from '../more-menu/MoreMenu';

export type PreviewTitle = {
  open: () => void;
  close: () => void;
};

export type IPreviewTitleProps = {
  disabled?: boolean;
  readonly?: boolean;
  multiline?: boolean;
  showEdit?: boolean;
  canEdit?: boolean;
  showDelete?: boolean;
  label?: string;
  leftSubLabel?: string;
  removeTopPadding?: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
  customFirstLevelActions?: CustomToolbarMappedAction[];
  customPreviewActions?: CustomToolbarMappedAction[];
  onChange?: (value: string) => void;
  onClick?: () => void;
  onVisibilityToggle?: (visible: boolean) => void;
  onClose?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onCloseClick?: React.MouseEventHandler<HTMLButtonElement>;
  onEditClicked?: React.MouseEventHandler;
  onDeleteClicked?: React.MouseEventHandler;
  onSettingsClicked?: React.MouseEventHandler;
  onOpenFilePageClicked?: () => void;
  onShareClicked?: () => void;
  convertProps?: Pick<IConvertPopoverProps, 'exclude' | 'onClick'>;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  onFullscreenClick?: () => void;
};

export const PreviewTitle = forwardRef(function PreviewTitle(
  props: IPreviewTitleProps,
  ref: ForwardedRef<PreviewTitle>
) {
  const {
    disabled = true,
    showEdit = true,
    canEdit = true,
    showDelete = true,
    readonly = false,
    multiline = false,
    label = '',
    children,
    actions,
    customFirstLevelActions = [],
    customPreviewActions = [],
    onChange,
    onClick,
    onClose,
    onCloseClick,
    onShareClicked,
    onEditClicked,
    onDeleteClicked,
    onSettingsClicked,
    leftSubLabel,
    removeTopPadding,
    onOpenFilePageClicked,
    convertProps,
    leftActions,
    rightActions,
    onFullscreenClick,
    onVisibilityToggle,
  } = props;

  const [editMode, setEditMode] = useState(false);
  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setEditMode(true);
      },
      close: () => {
        setEditMode(false);
      },
    }),
    []
  );

  const { t } = useTranslation();

  const isUpLg = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'));
  const isDownSm = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );
  const convertPopoverState = usePopoverState('bottom');

  const closeButton =
    (isDownSm || !onVisibilityToggle) && onCloseClick ? (
      <IconButton size="small" onClick={onCloseClick}>
        <CloseIcon />
      </IconButton>
    ) : null;

  const visibilityToggle =
    !isDownSm && onVisibilityToggle ? (
      <IconButton size="small" onClick={() => onVisibilityToggle(false)}>
        <ChevronRight />
      </IconButton>
    ) : null;

  const defaultFirstLevelActions: CustomToolbarMappedAction[] = [
    ...(onEditClicked && !editMode && showEdit
      ? [
          {
            title: t('COMMON.EDIT'),
            actionKey: 'edit',
            IconComponent: EditIcon,
            disabled: !canEdit,
            disableReason: !canEdit ? t('RIGHTS.MISSING') : undefined,
            onClick: onEditClicked,
          },
        ]
      : []),
  ];

  const defaultActions: CustomToolbarMappedAction[] = [
    ...(onShareClicked
      ? [
          {
            title: t('COMMON.SHARE'),
            actionKey: 'share',
            IconComponent: ShareIcon,
            onClick: onShareClicked,
          },
        ]
      : []),
    ...(onFullscreenClick
      ? [
          {
            title: t('COMMON.FULLSCREEN'),
            actionKey: 'fullscreen',
            IconComponent: Fullscreen,
            onClick: () => onFullscreenClick(),
          },
        ]
      : []),
    ...(onOpenFilePageClicked
      ? [
          {
            title: t('MASK.OPEN_IN_NEW_TAB'),
            actionKey: 'open-in-new-tab',
            IconComponent: OpenInNewIcon,
            onClick: onOpenFilePageClicked,
          },
        ]
      : []),
    ...(onDeleteClicked && showDelete
      ? [
          {
            title: t('COMMON.DELETE'),
            actionKey: 'delete',
            IconComponent: Delete,
            onClick: onDeleteClicked,
          },
        ]
      : []),
    ...(convertProps
      ? [
          {
            title: t('COMMON.CONVERT'),
            actionKey: 'convert',
            IconComponent: ConvertIcon,
            onClick: convertPopoverState.handleClick,
          },
        ]
      : []),
    ...(isUpLg && onSettingsClicked
      ? [
          {
            title: t('COMMON.SETTINGS'),
            actionKey: 'settings',
            IconComponent: SettingsIcon,
            onClick: onSettingsClicked,
          },
        ]
      : []),
  ];

  const moreMenuActions = [...customPreviewActions, ...defaultActions];

  return (
    <Stack
      className={styles.root}
      direction="row"
      alignItems="center"
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.vars.palette.ui4.main}`,
        paddingTop: removeTopPadding ? 0 : 'var(--w4a-safe-area-top, 0px)',
        zIndex: 1,
      })}
    >
      {closeButton}
      {visibilityToggle}

      <div
        style={{ height: multiline ? 'unset' : undefined }}
        className={styles.titleWrapper}
      >
        {leftSubLabel ? (
          <>
            <Typography variant="h4" className={styles.subTitle}>
              {leftSubLabel}
            </Typography>
            <Typography
              variant="h4"
              className={clsx(styles.subTitle, styles.divider)}
            >
              {'|'}
            </Typography>
          </>
        ) : null}
        {editMode ? (
          <OutlinedInput
            autoFocus={true}
            className={styles.input}
            defaultValue={label}
            multiline={multiline}
            classes={{ notchedOutline: styles.withoutOutline }}
            onBlur={(e) => {
              setEditMode(false);
              onClose && onClose(e);
            }}
            onChange={(e) => onChange?.(e.target.value)}
          />
        ) : (
          <div
            className={clsx(styles.triggerContainer, {
              [styles.readonly]: readonly,
              [styles.interactive]: true,
            })}
            onClick={() => {
              onClick && onClick();

              if (!disabled) {
                setEditMode(true);
              }
            }}
          >
            <Typography
              justifyContent="center"
              variant="h4"
              color="text.primary"
              className={styles.title}
              noWrap
              sx={(theme) => ({
                flex: 1,
                '&:last-of-type': {
                  marginRight: theme.spacing(2),
                },
              })}
            >
              {children}
            </Typography>
          </div>
        )}
      </div>

      {convertProps && (
        <ConvertPopover {...convertProps} popoverState={convertPopoverState} />
      )}

      {actions ? (
        <Stack direction="row">{actions}</Stack>
      ) : (
        <Stack direction="row" alignItems="center">
          {leftActions}
          {(customFirstLevelActions && customFirstLevelActions.length > 0
            ? customFirstLevelActions
            : defaultFirstLevelActions
          ).map((item) => (
            <Tooltip
              key={item.actionKey}
              activateForDisabled
              title={
                item.disabled && item.disableReason ? item.disableReason : ''
              }
              placement="top"
            >
              <IconButton
                onClick={!item.href ? item.onClick : undefined}
                href={item.href}
                color="primary"
                disabled={item.disabled}
              >
                <item.IconComponent />
              </IconButton>
            </Tooltip>
          ))}
          {moreMenuActions.length ? (
            <MoreMenu actions={moreMenuActions} />
          ) : null}
          {rightActions}
        </Stack>
      )}
    </Stack>
  );
});
